import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import api from "../../api";
import { useDispatch } from "react-redux";
import { setDialog, setLoading } from "slices/appSlice";
import {
  NEW_COMMENT,
  POET_AVALON_POE,
  USERGROUP_CODING,
  USERGROUP_CODING_REVIEW,
} from "../../constants/Constants";
import PolicyTypeBanner from "components/PolicyTypeBanner";
import FirstLineContainer from "components/common/FirstLineContainer";
import Drawers from "../../components/common/Drawers";
import GreyOutButton from "components/GreyOutButton/GreyOutButton";
import PolicyAdoption from "api/policyAdoption";
import { formErrorStrfromObj } from "utils/shared";
import ViewClobButton from "utils/ViewClobButton";

const TopPanelComponent = ({
  policyStateData,
  policyHeader,
  policyKey,
  healthPlanDisplayName,
  healthPlanKey,
  updatePolicyData,
  handleCancelAction,
  policyNumber,
  policyTitle,
  policyVersion,
  editable,
}) => {
  const [coverageCriteria, setCoverageCriteria] = useState(
    policyHeader?.TotalCoverageCriteria
  );
  const [policyData, setPolicyData] = useState({
    policyTranslator: "",
    policyCoder: "",
    translationReviewer: "",
    codingStatus: "",
    codingReviewer: "",
    translationStatus: "",
    reviewType: "",
    overallAdoptionDecision: "",
    lobAdoptionDecisionList: [],
    policyChangeControlXrefDto: {
      policyChangeControlXrefKey: null,
      changeControlText: "",
    },
  });
  const [policyCoders, setPolicyCoders] = useState([]);
  const [codingReviewers, setCodingReviewers] = useState([]);

  const [activeDrawer, setActiveDrawer] = useState(null);
  const [policyHistoryData, setPolicyHistoryData] = useState([]);
  const [ctReviewData, setCTReviewData] = useState([]);
  const [reviewData, setReviewData] = useState({
    ctReviewKey: undefined,
    results: "",
    comments: "",
    reviewType: "",
  });

  const dispatch = useDispatch();
  const handleCoverageCriteria = (event) => {
    const newValue = event.target.value.trim();
    if (/^\d{0,2}$/.test(newValue)) {
      if (
        newValue === "" ||
        (parseInt(newValue, 10) >= 0 && parseInt(newValue, 10) <= 99)
      ) {
        setCoverageCriteria(newValue);
      }
    }
  };

  const handlePolicyCoderChange = (event) => {
    setPolicyData({ ...policyData, policyCoder: event.target.value });
  };

  const handleCodingReviewChange = (event) => {
    setPolicyData({ ...policyData, codingReviewer: event.target.value });
  };

  const handleChangeControlChange = (event) => {
    setPolicyData((prevData) => ({
      ...prevData,
      policyChangeControlXrefDto: {
        ...prevData.policyChangeControlXrefDto,
        changeControlText: event.target.value.trim(),
      },
    }));
  };

  useEffect(() => {
    const fetchPolicyData = async () => {
      try {
        dispatch(setLoading(true));
        const response = await api.policyData.getPolicyData(
          healthPlanKey,
          policyKey
        );

        if (response.status === 200 && response.data != null) {
          setPolicyData({
            policyTranslator: response.data.policyTranslator,
            policyCoder: response.data.policyCoder,
            translationReviewer: response.data.translationReviewer,
            codingStatus: response.data.codingStatus,
            codingReviewer: response.data.codingReviewer,
            translationStatus: response.data.translationStatus,
            reviewType: response.data.reviewType,
            overallAdoptionDecision: response.data.overallAdoptionDecision,
            lobAdoptionDecisionList: response.data.lobAdoptionDecisionList,
            policyChangeControlXrefDto:
              response.data.policyChangeControlXrefDto !== null
                ? response.data.policyChangeControlXrefDto
                : { policyChangeControlXrefKey: null, changeControlText: "" },
          });
          setPolicyHistoryData(response.data.policyHistoryList);
          setCTReviewData(response.data.policyCTReviewList);
        } else throw new Error("API failed to return policy data.");
      } catch (e) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(e), title: "Error" })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchPolicyData();
  }, []);

  //Populate drop downss
  useEffect(() => {
    const fetchUsers = async () => {
      dispatch(setLoading(true));

      try {
        // Fetch the list of users for the dropdowns
        const usersResponse = await api.healthPlanConfig.fetchAllUsers();

        setPolicyCoders(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_CODING)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );

        setCodingReviewers(
          usersResponse.data.filter((user) => {
            let returnUser = false;

            if (
              user.userGroupName === POET_AVALON_POE &&
              user.poetUserAttributes.includes(USERGROUP_CODING_REVIEW)
            ) {
              returnUser = true;
            }

            return returnUser;
          })
        );
      } catch (e) {
        dispatch(
          setDialog({
            message: e.message,
            title: "Error: Could not fetch users",
          })
        );
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    let description = "";
    description =
      policyData.codingStatus.toLowerCase() === "peer review"
        ? "Coding"
        : policyData.codingStatus.toLowerCase() === "pdc review"
          ? "PDC Coding"
          : "Coding";

    // Find the first row where submittedDate is null or empty and reviewType matches description
    const matchingRow = ctReviewData?.find(
      (row) =>
        (!row.submittedDate || row.submittedDate === "") &&
        row.reviewType === description
    );

    setReviewData((prev) => ({
      ...prev,
      reviewType: description,
      ctReviewKey: matchingRow?.ctReviewKey,
      results: matchingRow?.results || "",
      comments: matchingRow?.comments || "",
    }));
  }, [ctReviewData]);

  const handleDrawerButtonClick = (drawerType) => {
    setActiveDrawer(drawerType);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  const submitForReview = (submitForReview = true) => {
    const updateAssignessReviewerDto =
      !policyData.policyCoder && !policyData.codingReviewer
        ? null
        : {
            healthPlanKey: healthPlanKey,
            policyKey: policyKey,
            coding: policyData.policyCoder || null,
            codingReviewer: policyData.codingReviewer || null,
            policyStage: "Coding",
          };
    updatePolicyData(
      submitForReview,
      policyHistoryData.filter((history) => history.type === NEW_COMMENT),
      reviewData,
      updateAssignessReviewerDto,
      policyData.policyChangeControlXrefDto
    );
  };
  const disableAdoptionDetailsDrawer = () => {
    return policyData.overallAdoptionDecision === null;
  };
  const policyTypeBannerText =
    `${healthPlanDisplayName} - ${policyVersion}` +
    (policyData.overallAdoptionDecision
      ? ` - ${policyData.overallAdoptionDecision}`
      : "");

  return (
    <>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Policy Coding:
          </Box>{" "}
          {policyNumber} - {policyTitle}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: "right", flex: 1 }}>
          {policyHeader.PolicyType}
        </Typography>
        <ViewClobButton />
      </FirstLineContainer>

      <PolicyTypeBanner
        bannerText={policyTypeBannerText}
        handleCancel={handleCancelAction}
        handleDraft={() => submitForReview(false)}
        handleReview={() => submitForReview(true)}
        editable={editable}
      />
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        {/* Left Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Review Type:
            </Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyStateData?.cowName} {policyStateData?.quarter}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Change Control:
            </Typography>
            <TextField
              fullWidth
              sx={{ flex: 1 }}
              value={policyData.policyChangeControlXrefDto?.changeControlText}
              onChange={(e) => handleChangeControlChange(e)}
            ></TextField>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{ flex: 0.5 }}
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Total Coverage Criteria:
            </Typography>
            <TextField
              inputProps={{ maxLength: 2 }}
              fullWidth
              sx={{ flex: 1 }}
              onChange={handleCoverageCriteria}
              value={coverageCriteria}
            />
          </Box>
        </Box>
        {/* Center Column */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mr: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Coding Status:
            </Typography>
            <Typography sx={{ flex: 1 }} component="span">
              {policyData.codingStatus}
            </Typography>
          </Box>
          <FormControl
            fullWidth
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Policy Coder:
            </Typography>
            <Select
              sx={{ flex: 1 }}
              value={policyData.policyCoder}
              onChange={handlePolicyCoderChange}
            >
              {policyCoders.map((coder) => (
                <MenuItem key={coder.userKey} value={coder.userEmailAddress}>
                  {`${coder.userFirstName} ${coder.userLastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            fullWidth
            variant="outlined"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              component="span"
              fontWeight="bold"
              whiteSpace="nowrap"
              sx={{ flex: 0.5 }}
            >
              Coding Reviewer:
            </Typography>
            <Select
              sx={{ flex: 1 }}
              value={policyData.codingReviewer}
              onChange={handleCodingReviewChange}
            >
              {codingReviewers.map((reviewer) => (
                <MenuItem
                  key={reviewer.userKey}
                  value={reviewer.userEmailAddress}
                >
                  {`${reviewer.userFirstName} ${reviewer.userLastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Right Column */}
        <Box sx={{ mr: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <GreyOutButton
            variant="outlined"
            size="small"
            disabled={disableAdoptionDetailsDrawer()}
            onClick={() => handleDrawerButtonClick("adoptionDetails")}
          >
            Adoption Details
          </GreyOutButton>
          <GreyOutButton
            variant="outlined"
            size="small"
            disabled={
              policyData.codingStatus === "In Progress" ||
              policyData.codingStatus === "Assigned"
            }
            onClick={() => handleDrawerButtonClick("ctReviews")}
          >
            C&T Reviews
          </GreyOutButton>
          <GreyOutButton
            size="small"
            variant="outlined"
            onClick={() => handleDrawerButtonClick("policyHistory")}
          >
            Policy History
          </GreyOutButton>
        </Box>
      </Box>
      {/* Use the Drawers component */}
      {activeDrawer && (
        <Drawers
          policyVersion={policyVersion}
          editable={editable}
          activeDrawer={activeDrawer}
          closeDrawer={closeDrawer}
          healthPlanKey={healthPlanKey}
          policyKey={policyKey}
          status={policyData.codingStatus}
          lobAdoptionDecisions={policyData.lobAdoptionDecisionList}
          policyHistoryData={policyHistoryData}
          setPolicyHistoryData={setPolicyHistoryData}
          ctReviewData={ctReviewData}
          reviewData={reviewData}
          setReviewData={setReviewData}
        />
      )}
    </>
  );
};

export default TopPanelComponent;
